import {
    buildPreContractLivePpwQuotes,
    pickRecommendedPartnerQuote,
    sortPartnerQuotesForDisplay,
    type PreContractPpwMetricsOverride,
    type PpwPartnerQuote,
} from './preContractPpwPricing';
import { financePartnerKeysEqual } from '../../components/Layout-Components/Editor-Layout/oneBUTTONDashboard/utils/finance';
import quoteServices from '../../services/oneButtonServices/quoteServices';
import {
    fetchRoofBatteryPricingOffers,
    type PreContractPricingOffer,
} from './preContractPricing';
import type { PreContractIntakeForm, PreContractProduct } from './preContractTypes';

export type PreContractDesignPartnerRecommendation = {
    product: PreContractProduct;
    partner: string;
    financeMethod: string;
    monthly: number | null;
    available: boolean;
    reason?: string;
};

export type PreContractProductPricingOption = {
    partner: string;
    financeMethod: string;
    monthly: number | null;
    epc?: number | null;
    ppw?: number | null;
    /** Finance term in months when known (e.g. loan/lease length). */
    term?: number | null;
    /** APR percent when known. */
    apr?: number | null;
    /** PPA escalator (decimal) for solar partners. */
    escalator?: number | null;
    available: boolean;
    reason?: string;
    recommended?: boolean;
};

export type PreContractProductPricingBundle = {
    product: PreContractProduct;
    recommended: PreContractDesignPartnerRecommendation;
    options: PreContractProductPricingOption[];
};

export type PreContractPartnerSyncState = 'unset' | 'match' | 'mismatch';

export function partnerKeysEqual(a: string | null | undefined, b: string | null | undefined): boolean {
    return financePartnerKeysEqual(a, b);
}

export function financeMethodsEqual(a: string | null | undefined, b: string | null | undefined): boolean {
    const left = String(a ?? '').trim().toLowerCase();
    const right = String(b ?? '').trim().toLowerCase();
    if (!left || !right) return true;
    return left === right;
}

export function resolvePartnerSyncState(
    saved: { partner: string | null; method: string | null },
    recommended: PreContractDesignPartnerRecommendation | null | undefined,
): PreContractPartnerSyncState {
    if (!recommended?.available || !recommended.partner.trim()) return 'unset';
    if (!saved.partner?.trim()) return 'mismatch';
    const partnerMatch = partnerKeysEqual(saved.partner, recommended.partner);
    const methodMatch = financeMethodsEqual(saved.method, recommended.financeMethod);
    return partnerMatch && methodMatch ? 'match' : 'mismatch';
}

export function resolveHubPartnerLabel(
    partner: string,
    options: Array<{ value: string; label?: string }>,
): string {
    const trimmed = partner.trim();
    const match = options.find(
        (option) =>
            partnerKeysEqual(option.value, trimmed) ||
            partnerKeysEqual(option.label ?? '', trimmed),
    );
    return match?.value ?? trimmed;
}

export function formatDesignPartnerLine(
    recommendation: PreContractDesignPartnerRecommendation | null | undefined,
): string {
    if (!recommendation?.partner?.trim()) return 'Partner not set';
    const partner = recommendation.partner.trim();
    if (recommendation.financeMethod?.trim()) {
        return `${partner} · ${recommendation.financeMethod}`;
    }
    return partner;
}

export function partnerSyncStateLabel(state: PreContractPartnerSyncState): string {
    if (state === 'match') return 'Synced';
    if (state === 'mismatch') return 'Update needed';
    return 'Not saved';
}

export function optionToDesignRecommendation(
    product: PreContractProduct,
    option: PreContractProductPricingOption,
): PreContractDesignPartnerRecommendation {
    return {
        product,
        partner: option.partner,
        financeMethod: option.financeMethod,
        monthly: option.monthly,
        available: option.available,
        reason: option.reason,
    };
}

export function pricingOptionsMatch(
    a: PreContractProductPricingOption | null | undefined,
    b: PreContractProductPricingOption | null | undefined,
): boolean {
    if (!a || !b) return false;
    return (
        partnerKeysEqual(a.partner, b.partner) && financeMethodsEqual(a.financeMethod, b.financeMethod)
    );
}

/** Prefer the pricing option that matches the partner already tied to the opportunity product. */
export function findPricingOptionForSavedPartner(
    options: PreContractProductPricingOption[],
    saved: { partner: string | null | undefined; method?: string | null | undefined },
): PreContractProductPricingOption | undefined {
    const partner = String(saved.partner ?? '').trim();
    if (!partner || options.length === 0) return undefined;

    const partnerMatches = options.filter((option) => partnerKeysEqual(option.partner, partner));
    if (partnerMatches.length === 0) return undefined;

    const method = String(saved.method ?? '').trim();
    if (method) {
        const methodMatch = partnerMatches.find((option) =>
            financeMethodsEqual(option.financeMethod, method),
        );
        if (methodMatch) return methodMatch;
    }

    return (
        partnerMatches.find((option) => option.available) ??
        partnerMatches[0]
    );
}

function ppwQuoteToOption(quote: PpwPartnerQuote, recommended: boolean): PreContractProductPricingOption {
    return {
        partner: quote.partner.trim(),
        financeMethod: '',
        monthly: quote.estimatedMonthly,
        epc: quote.epc,
        ppw: quote.ppw,
        escalator: quote.escalator,
        available: Boolean(quote.available && quote.partner),
        reason: quote.available === false ? quote.reason : undefined,
        recommended,
    };
}

function offerToOption(offer: PreContractPricingOffer, recommended: boolean): PreContractProductPricingOption {
    if (offer.error) {
        return {
            partner: offer.partner.trim(),
            financeMethod: offer.financeMethod,
            monthly: null,
            available: false,
            reason: offer.error,
            recommended,
        };
    }
    return {
        partner: offer.partner.trim(),
        financeMethod: offer.financeMethod,
        monthly: offer.monthly > 0 ? offer.monthly : null,
        term: offer.term,
        apr: offer.apr,
        available: Boolean(offer.partner && offer.financeMethod),
        recommended,
    };
}

function recommendationFromOption(
    product: PreContractProduct,
    option: PreContractProductPricingOption,
): PreContractDesignPartnerRecommendation {
    return optionToDesignRecommendation(product, option);
}

function sortPricingOptions(options: PreContractProductPricingOption[]): PreContractProductPricingOption[] {
    return [...options].sort((a, b) => {
        if (a.recommended && !b.recommended) return -1;
        if (b.recommended && !a.recommended) return 1;
        if (a.available && !b.available) return -1;
        if (b.available && !a.available) return 1;
        const aMonthly = a.monthly ?? Number.POSITIVE_INFINITY;
        const bMonthly = b.monthly ?? Number.POSITIVE_INFINITY;
        if (aMonthly !== bMonthly) return aMonthly - bMonthly;
        return a.partner.localeCompare(b.partner);
    });
}

export function buildSolarDesignPartnerRecommendation(
    form: PreContractIntakeForm,
    metricsOverride: PreContractPpwMetricsOverride | null,
    preferEnfinPartner = false,
): PreContractDesignPartnerRecommendation {
    const bundle = buildSolarPricingBundle(form, metricsOverride, preferEnfinPartner);
    return bundle.recommended;
}

function buildSolarPricingBundle(
    form: PreContractIntakeForm,
    metricsOverride: PreContractPpwMetricsOverride | null,
    preferEnfinPartner = false,
): PreContractProductPricingBundle {
    const result = buildPreContractLivePpwQuotes(form, 0.0299, null, metricsOverride);
    const best = pickRecommendedPartnerQuote(result.quotes, { preferEnfin: preferEnfinPartner });
    const options = sortPricingOptions(
        sortPartnerQuotesForDisplay(result.quotes, { preferEnfin: preferEnfinPartner }).map((quote) =>
            ppwQuoteToOption(quote, quote.partner === best?.partner),
        ),
    );
    const recommendedOption =
        options.find((option) => option.recommended && option.available) ??
        options.find((option) => option.available) ??
        options[0] ?? {
            partner: '',
            financeMethod: '',
            monthly: null,
            available: false,
            reason: 'No pricing offers available for this design',
        };

    return {
        product: 'Solar',
        recommended: recommendationFromOption('Solar', recommendedOption),
        options,
    };
}

function buildRoofBatteryBundle(
    product: 'Roof' | 'Battery',
    offers: PreContractPricingOffer[],
): PreContractProductPricingBundle {
    const productOffers = offers.filter((offer) => offer.productType === product);
    const best = productOffers
        .filter((offer) => !offer.error && offer.monthly > 0)
        .reduce<PreContractPricingOffer | null>(
            (winner, offer) => (!winner || offer.monthly < winner.monthly ? offer : winner),
            null,
        );

    const options = sortPricingOptions(
        productOffers.map((offer) =>
            offerToOption(offer, Boolean(best && offer.partner === best.partner && offer.financeMethod === best.financeMethod)),
        ),
    );

    const recommendedOption =
        options.find((option) => option.recommended && option.available) ??
        options.find((option) => option.available) ??
        options[0] ?? {
            partner: '',
            financeMethod: '',
            monthly: null,
            available: false,
            reason: 'No pricing offers available',
        };

    return {
        product,
        recommended: recommendationFromOption(product, recommendedOption),
        options,
    };
}

export async function fetchProductPricingBundles(
    form: PreContractIntakeForm,
    metricsOverride: PreContractPpwMetricsOverride | null,
    preferEnfinPartner = false,
): Promise<Partial<Record<PreContractProduct, PreContractProductPricingBundle>>> {
    const out: Partial<Record<PreContractProduct, PreContractProductPricingBundle>> = {};

    if (form.products.Solar) {
        out.Solar = buildSolarPricingBundle(form, metricsOverride, preferEnfinPartner);
    }

    const roofBatteryProducts = (['Roof', 'Battery'] as const).filter((product) => form.products[product]);
    if (roofBatteryProducts.length === 0) return out;

    try {
        const offers = await fetchRoofBatteryPricingOffers(form);
        for (const product of roofBatteryProducts) {
            out[product] = buildRoofBatteryBundle(product, offers);
        }
    } catch (error) {
        if (quoteServices.isPricingRequestAborted(error)) {
            throw error;
        }
        const message = error instanceof Error ? error.message : 'Pricing unavailable';
        for (const product of roofBatteryProducts) {
            out[product] = {
                product,
                recommended: {
                    product,
                    partner: '',
                    financeMethod: '',
                    monthly: null,
                    available: false,
                    reason: message,
                },
                options: [],
            };
        }
    }

    return out;
}

/** @deprecated Use fetchProductPricingBundles — kept for callers that only need the top pick. */
export async function fetchDesignPartnerRecommendations(
    form: PreContractIntakeForm,
    metricsOverride: PreContractPpwMetricsOverride | null,
    preferEnfinPartner = false,
): Promise<Partial<Record<PreContractProduct, PreContractDesignPartnerRecommendation>>> {
    const bundles = await fetchProductPricingBundles(form, metricsOverride, preferEnfinPartner);
    const out: Partial<Record<PreContractProduct, PreContractDesignPartnerRecommendation>> = {};
    for (const [product, bundle] of Object.entries(bundles) as Array<
        [PreContractProduct, PreContractProductPricingBundle]
    >) {
        out[product] = bundle.recommended;
    }
    return out;
}
